import React, { useRef, useEffect, useState } from "react";
import { Select, Button, DatePicker, Radio, Form, message, Spin } from "antd";
import * as echarts from "echarts";
import moment from "moment";
import Analysisapi from "@/api/analysis";

const { Option } = Select;
const { RangePicker } = DatePicker;

// const mockX = ['1月', '2月', '3月', '4月', '5月'];
// const mockSeries = [
//   { name: '电器柜', data: [100, 140, 230, 100, 130] },
//   { name: '工艺段', data: [150, 100, 200, 140, 100] },
//   { name: '重点设备', data: [136, 112, 230, 224, 105] },
// ];

export default function Pandect() {
  const chartRef = useRef(null);

  const [statType, setStatType] = useState(1); // 1:月 2:季度 3:年
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [xData, setXData] = useState([]); // 横坐标
  const [series, setSeries] = useState([]); // series
  const [energyTypeList, setEnergyTypeList] = useState([]);
  const [lineTypeList, setLineTypeList] = useState([]);
  // 表单初始值
  // 能耗类型列表加载后设置默认表单值
  useEffect(() => {
    if (lineTypeList.length > 0) {
      const start = moment().startOf("month");
      const end = moment().endOf("month");
      form.setFieldsValue({
        type: null,
        statType: 1,
        date: [start, end],
      });
      fetchChartData({
        energyId: null,
        censusType: 1,
        startTime: start.format("YYYY-MM-DD HH:mm:ss"),
        endTime: end.format("YYYY-MM-DD HH:mm:ss"),
      });
    }
  }, [lineTypeList]);

  // 日期格式化 LocalDateTime
  function formatDate(date) {
    if (!date) return "";
    return moment(date).format("YYYY-MM-DD HH:mm:ss");
  }

  const selectEnergyList = () => {
    Analysisapi.selectEnergyList().then((res) => {
      if (res.status == 0 && res.data) {
        setLineTypeList(res.data);
      }
    });
  }

  /**
   * 能耗数据格式转换
   * @param {Array} originalData - 原始能耗数据数组
   * @returns {Array} 整理后（时间排序+补0）的标准格式数据
   */
  function formatEnergyData(originalData) {
    // 1. 入参校验：非数组返回空数组
    if (!Array.isArray(originalData)) {
      console.warn('原始数据必须为数组格式');
      return [];
    }

    // 2. 提取所有唯一设备信息（全局设备列表，用于补0）
    // 结构：{ deviceClassifyId: 唯一ID, deviceClassifyName: 设备名称 }
    const allDevices = Array.from(
      new Map(
        originalData.map(item => [
          item.energyId,
          { deviceClassifyId: item.energyId, deviceClassifyName: item.name }
        ])
      ).values()
    );
    // 无设备数据直接返回空
    if (allDevices.length === 0) return [];

    // 3. 提取所有唯一时间并按【年月正序】排序（格式：YYYY-MM）
    const sortedTimeList = Array.from(new Set(originalData.map(item => item.timeStr)))
      .sort((a, b) => {
        if(a.length == 7){
          new Date(a + '-01') - new Date(b + '-01')
        }else {
          new Date(a) - new Date(b)
        }
      });

    // 4. 按时间分组原始数据（key: timeStr, value: 该时间下的设备数据）
    const timeGroupMap = {};
    originalData.forEach(item => {
      const { timeStr = '', energyId = '', totalValue = 0 } = item || {};
      if (!timeStr) return;
      if (!timeGroupMap[timeStr]) timeGroupMap[timeStr] = {};
      // 按设备ID存储，方便后续快速匹配
      timeGroupMap[timeStr][energyId] = totalValue;
    });

    // 5. 构建最终结果（时间排序+缺失设备补0）
    const result = sortedTimeList.map(timeIcon => {
      // 该时间下的原始设备能耗数据（key: 设备ID, value: 能耗值）
      const currentTimeEnergy = timeGroupMap[timeIcon] || {};
      // 生成该时间的完整能耗数组：有数据用原值，无数据补0
      const energyArr = allDevices.map(device => ({
        ...device, // 继承设备ID和名称
        energyAll: currentTimeEnergy[device.deviceClassifyId] || 0 // 补0核心逻辑
      }));
      return { timeIcon, energyArr };
    });

    return result;
  }

  // 拉取能耗总览数据
  const fetchChartData = async (params) => {
    setLoading(true);
    try {
      const res = await Analysisapi.selectEnergyTotal(params);
      if (res?.status === "0" && Array.isArray(res.data)) {
        // 适配 [{timeIcon, energyArr:[{deviceClassifyId, deviceClassifyName, energyAll}]}, ...]
        // 1. x轴为 timeIcon
        // 2. series 为每个 deviceClassifyName

        let resData = formatEnergyData(res.data);

        const xAxis = resData.map(item => item.timeIcon);
        console.log(resData);
        // 收集所有设备类型
        const deviceMap = {};
        resData.forEach(item => {
          (item.energyArr || []).forEach(e => {
            deviceMap[e.deviceClassifyId] = e.deviceClassifyName;
          });
        });
        const deviceIds = Object.keys(deviceMap);
        // 构造 series
        const series = deviceIds.map(id => ({
          name: deviceMap[id],
          data: resData.map(item => {
            const found = (item.energyArr || []).find(e => e.deviceClassifyId == id);
            return found ? found.energyAll : 0;
          })
        }));
        setXData(xAxis);
        setSeries(series);
      } else {
        message.warning("能耗总览接口无数据");
        setXData([]);
        setSeries([]);
      }
    } catch (e) {
      message.error("能耗总览接口请求失败");
      setXData([]);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取能耗类型列表
  const fetchEnergyTypeList = async () => {
    try {
      const res1 = await Analysisapi.deviceClassifyList({
        classifyName: "",
        pageNum: 1,
        pageSize: 100,
      });
      setEnergyTypeList(res1?.data?.data || []);
    } catch {
      setEnergyTypeList([]);
    }
  };

  // 查询按钮
  const onSearch = () => {
    form.validateFields().then((values) => {
      const { type, statType, date } = values;
      fetchChartData({
        energyId: type,
        censusType: statType,
        startTime: formatDate(date?.[0]),
        endTime: formatDate(date?.[1]),
      });
    });
  };

  // 重置按钮
  const onReset = () => {
    const start = moment().startOf("month");
    const end = moment().endOf("month");
    form.setFieldsValue({
      type: 1,
      statType: 1,
      date: [start, end],
    });
    fetchChartData({
      energyId: null,
      censusType: 1,
      startTime: start.format("YYYY-MM-DD HH:mm:ss"),
      endTime: end.format("YYYY-MM-DD HH:mm:ss"),
    });
  };

  useEffect(() => {
    if (chartRef.current && xData.length && series.length) {
      const chart = echarts.init(chartRef.current);
      chart.setOption({
        tooltip: { trigger: "axis" },
        legend: { data: series.map((s) => s.name), top: 40 },
        xAxis: { type: "category", data: xData },
        yAxis: { type: "value" },
        series: series.map((s) => ({
          name: s.name,
          type: "bar",
          data: s.data,
          label: {
            show: true,
            position: "top",
            color: "#3fa7ff",
            fontSize: 14,
          },
          barMaxWidth: 32,
        })),
        grid: { top: 80, left: 70, right: 20, bottom: 30 },
      });
      return () => chart.dispose();
    }
  }, [xData, series]);

  useEffect(() => {
    fetchEnergyTypeList();
    selectEnergyList();
  }, []);

  return (
    <div style={{ background: "#fff", padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{
          marginBottom: 24,
          flexWrap: "wrap",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Form.Item label="能耗类型" name="type" style={{ minWidth: 180 }}>
          <Select
            style={{ width: 160 }}
            placeholder="请选择能耗类型"
            loading={!lineTypeList.length}
            allowClear
          >
            <Option value={null}>全部类型</Option>
            {lineTypeList.map((item) => (
              <Option value={item.id} key={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="统计类型" name="statType">
          <Radio.Group>
            <Radio.Button value={1}>日</Radio.Button>
            <Radio.Button value={2}>周</Radio.Button>
            <Radio.Button value={3}>月</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="时间" name="date">
          <RangePicker
            style={{ minWidth: 220 }}
            showTime={{ format: "HH:mm:ss" }}
            format="YYYY-MM-DD HH:mm:ss"
            allowClear={false}
          />
        </Form.Item>
        <Form.Item>
          <Button onClick={onReset}>重置</Button>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={onSearch}>
            查询
          </Button>
        </Form.Item>
      </Form>
      <div style={{ height: 480, width: "100%", position: 'relative' }}>
        <Spin spinning={loading} tip="加载中..." size="large">
          <div ref={chartRef} style={{ width: "100%", height: 480 }} />
        </Spin>
      </div>
    </div>
  );
}
